import { FlexContainer } from '@/styles/Containers';
import SemesterCalculator from '@/components/calculator/SemesterCalculator';
import Layout from '@/styles/Layout';
import { CustomizableHeading, Letter } from '@/styles/Text';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import API_URL from '../config';
import useSize from '@/utils/useSize';
import ExpandMore from '../assets/icons/ExpandMore.svg';
import ExpandLess from '../assets/icons/ExpandLess.svg';
import { CalculatorSemesterProps, CalculatorCourseProps } from '@/components/calculator/CalculatorTypes';
import { gradeValues } from '@/components/calculator/CalculatorTypes';
import { useAuth } from '@/utils/auth';
import Loading from '@/components/Loading'; //ny

const TotalGradeCircle = styled.div`
  position: absolute;
  bottom: 11rem;
  right: 6rem;
  background-color: ${({ theme }) => theme.color.darkBlue};
  border-radius: 50%;
  height: 10rem;
  width: 10rem;
  box-shadow: 0.6rem 0rem 10px 2px #192f3e;
  z-index: 2;
  & * {
    color: ${({ theme }) => theme.color.white};
  }
`;

export const Icon = styled.img`
  width: 2.5rem;
  position: absolute;
  filter: invert(1);
  top: -0.1rem;
  right: 0.01rem;
`;

const MobileTotalGradeContainer = styled.div<{ isExpanded: boolean }>`
  position: fixed;
  bottom: 0rem;
  height: ${({ isExpanded }) => (isExpanded ? '12rem' : '6rem')};
  width: 100%;
  background-color: ${({ theme }) => theme.color.darkBlue};
  z-index: 2;
`;

export const CollapseButton = styled.button`
  position: absolute;
  top: -1.5rem;
  left: 50%;
  transform: translateX(-50%);
  align-items: center;
  background-color: ${({ theme }) => theme.color.darkBlue};
  cursor: pointer;
  border: none;
  border-radius: 45%;
  width: 2.5rem;
  height: 3rem;
  margin-right: 0.5rem;
`;

type SemesterGrade = {
  semester: string;
  courses: number;
  averageGrade: number;
};

interface Course {
  course_code: string;
  course_name_norwegian: string;
  semester: string;
  course_credit: number;
}

const CalculatorPage = ({ semesters }: { semesters?: CalculatorSemesterProps[] }) => {
  const [semesterGrades, setSemesterGrades] = useState<SemesterGrade[]>([]);
  const [totalAverageGrade, setTotalAverageGrade] = useState<number>(0);
  const [myCourses, setMyCourses] = useState<CalculatorSemesterProps[]>([]);
  const [totalCoursePoints, setTotalCoursePoints] = useState<number>(0);
  const { isMobile, size } = useSize();
  const [isExpanded, setIsExpanded] = useState(false);
  const { isAuthenticated } = useAuth();

  const {
    isSuccess: isSemesterDataSuccess,
    isError: isSemesterDataError,
    error: semesterDataError,
    isFetching: isSemesterDataFetching,
  } = useQuery(
    ['calculatorCourses'],
    async () => {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      const response = await axios.get(`${API_URL}/course/me/`);
      const courses: Course[] = response.data;
      const groupedCoursesBySemester = courses.reduce((coursesList: { [key: string]: Course[] }, course: Course) => {
        const key = course.semester;
        if (!coursesList[key]) {
          coursesList[key] = [];
        }
        coursesList[key].push(course);
        return coursesList;
      }, {});

      const transformedData: CalculatorSemesterProps[] = Object.keys(groupedCoursesBySemester).map((semester) => ({
        semester,
        calculatorCourses: groupedCoursesBySemester[semester].map((course: Course) => ({
          courseCode: course.course_code,
          courseName: course.course_name_norwegian,
          coursePoint: course.course_credit,
          grade: gradesData?.find((grade: { course: string }) => grade.course === course.course_code)?.grade || -2,
        })),
        grade: -2,
      }));
      setMyCourses(transformedData);
      return transformedData;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: gradesData,
    isSuccess: isGradesSuccess,
    isFetching: isGradesFetching,
  } = useQuery(
    ['grades'],
    async () => {
      const response = await axios.get(`${API_URL}/calculator/get_grades/`);
      const grades = Array.isArray(response.data) ? response.data : [response.data];
      const updatedCourses = myCourses.map((semester) => {
        const updatedCalculatorCourses = semester.calculatorCourses.map((course) => {
          const grade = grades.find((g) => g.course === course.courseCode);
          return grade ? { ...course, grade: grade.grade } : course;
        });
        return { ...semester, calculatorCourses: updatedCalculatorCourses };
      });

      setMyCourses(updatedCourses);
      return response.data;
    },
    { enabled: isSemesterDataSuccess && myCourses.length > 0, refetchOnWindowFocus: false },
  );

  const fromGradeToLetter = (grade: number): string => {
    const letterGrade = Object.keys(gradeValues).find((key) => gradeValues[key] === Math.round(grade));
    return letterGrade || 'F';
  };

  const handleSetGrade = async (semester: string, grade: number, courses: CalculatorCourseProps[]) => {
    const validCourses = courses.filter((course) => course.grade > 0);
    const totalAverageGrade = validCourses.reduce((total, course) => total + course.grade, 0);
    const coursesWithGrade = validCourses.length;
    setSemesterGrades((prevGrades) => {
      const updatedGrades = prevGrades.map((item) => (item.semester === semester ? { ...item, averageGrade: grade, courses: coursesWithGrade } : item));
      if (!updatedGrades.find((item) => item.semester === semester)) {
        updatedGrades.push({ semester, averageGrade: totalAverageGrade, courses: coursesWithGrade });
      }
      return updatedGrades;
    });
  };

  const updateTotalCoursePoints = (points: number) => {
    setTotalCoursePoints((prevPoints) => prevPoints + points);
  };

  useMemo(() => {
    if (isGradesSuccess && myCourses.length > 0) {
      const filteredSemesterGrades = semesterGrades.filter(({ averageGrade }) => averageGrade !== 0 && averageGrade !== -1 && averageGrade !== -2);

      if (filteredSemesterGrades.length > 0) {
        const totalGrade = filteredSemesterGrades.reduce((total, { averageGrade, courses }) => total + averageGrade * courses, 0);
        const totalCourses = filteredSemesterGrades.reduce((total, { courses }) => total + courses, 0);
        const newTotalAverageGrade = totalCourses === 0 ? 0 : Math.round((totalGrade / totalCourses) * 10) / 10;
        if (newTotalAverageGrade !== totalAverageGrade) {
          setTotalAverageGrade(newTotalAverageGrade);
          if (newTotalAverageGrade !== 0) {
            localStorage.setItem('totalAverageGrade', newTotalAverageGrade.toString());
          }
        }
      }
    }
  }, [isGradesSuccess, myCourses, semesterGrades, totalAverageGrade]);

  if (!isAuthenticated) {
    return (
      <Layout padding='5% 10%'>
        <CustomizableHeading headingLevel='h1' option='bold' mobileFontSize='2rem' margin='1rem'>
          Logg inn for å bruke snittkalkulatoren
        </CustomizableHeading>
      </Layout>
    );
  }

  if (isSemesterDataError) {
    return (
      <Layout padding='5% 10%'>
        <CustomizableHeading headingLevel='h1' option='bold' mobileFontSize='2rem' margin='1rem'>
          Kunne ikke hente data
        </CustomizableHeading>
        <Letter fontSize='1.5rem' margin='0.5rem 0' color='red' mobileFontSize='1rem'>
          {axios.isAxiosError(semesterDataError) && semesterDataError.response
            ? `Error: ${semesterDataError.response.statusText}`
            : 'Noe gikk galt. Vennligst prøv igjen senere.'}
        </Letter>
      </Layout>
    );
  }

  return (
    <>
      <Layout padding='5% 10%'>
        <CustomizableHeading fontSize='45' textAlign='left' option='bold' headingLevel='h1'>
          Snittkalkulator
        </CustomizableHeading>
        <FlexContainer flexDirection='column' gap='3rem' justifyContent='center'>
          {!isGradesSuccess || isSemesterDataFetching || isGradesFetching ? (
            <Loading />
          ) : (
            myCourses &&
            myCourses.map((semester) => (
              <SemesterCalculator
                key={semester.semester}
                semester={semester.semester}
                courses={semester.calculatorCourses}
                onGradeChange={handleSetGrade}
                semesters={semesters}
                updateTotalCoursePoints={updateTotalCoursePoints}
              />
            ))
          )}
        </FlexContainer>
      </Layout>

      {!isMobile && (
        <div style={{ position: 'relative' }}>
          <TotalGradeCircle>
            <Letter color='light grey' fontSize='1rem' margin='2rem 0.5rem 0rem 0rem' textAlign=''>
              Snittet ditt er:
            </Letter>
            <Letter fontSize='64px' margin='0 0 1rem 0 ' fontWeight={600} color='white'>
              {totalAverageGrade}
            </Letter>
          </TotalGradeCircle>
        </div>
      )}
      {isMobile && (
        <MobileTotalGradeContainer isExpanded={isExpanded}>
          <CollapseButton onClick={() => setIsExpanded(!isExpanded)}>
            <Icon src={isExpanded ? ExpandMore : ExpandLess} alt='Toggle' style={{ color: 'white' }} />
          </CollapseButton>
          <FlexContainer>
            <Letter fontSize='1rem' margin='1rem 2rem' textAlign='left' color='white'>
              Snittet ditt er:
            </Letter>
            <Letter fontSize='2.4rem' color='white' fontWeight={1000} margin='1rem 3.5rem' textAlign='right'>
              {totalAverageGrade} / {fromGradeToLetter(totalAverageGrade)}
            </Letter>
          </FlexContainer>
        </MobileTotalGradeContainer>
      )}
      {isMobile && isExpanded && (
        <MobileTotalGradeContainer isExpanded={isExpanded}>
          <CollapseButton onClick={() => setIsExpanded(!isExpanded)}>
            <Icon src={isExpanded ? ExpandMore : ExpandLess} alt='Toggle' />
          </CollapseButton>
          <FlexContainer>
            <Letter fontSize='1rem' margin='2.5rem 2rem' textAlign='left' color='white'>
              Snittet ditt er:
            </Letter>
            <Letter fontSize='2.4rem' color='white' fontWeight={1000} margin='1rem 4rem' textAlign='right'>
              {totalAverageGrade} / {fromGradeToLetter(totalAverageGrade)}
            </Letter>
          </FlexContainer>
          <FlexContainer>
            <Letter fontSize='1rem' margin='0.2rem 2rem' textAlign='left' color='white'>
              Ditt totale studiepoeng er:
            </Letter>
            <Letter fontSize='2.4rem' color='white' fontWeight={1000} margin='-1.1rem 0.5rem' textAlign='right'>
              {totalCoursePoints}
            </Letter>
          </FlexContainer>
        </MobileTotalGradeContainer>
      )}
    </>
  );
};

export default CalculatorPage;
